import React, { useState, useEffect } from 'react'
import { css } from '@emotion/react'
import lunr from 'lunr'
import Select from 'react-select'
import usePickupDestination from './usePickupDestination'
import { DestinationsContext } from './Destinations'
import DestinationsModal from './DestinationsModal'
import { isMobile } from 'react-device-detect'

// TODO: Is this required?
declare global {
  interface Window {
    __LUNR__: {
      readonly [language: string]: {
        readonly index: lunr.Index
        readonly store: {
          readonly [key: string]: any
        }
      }
    }
  }
}

export interface SearchResult extends lunr.Index.Result {
  readonly label: string
  readonly value: string
  readonly name: string
  readonly suffix: string
}

export interface Destination {
  readonly label: string
  readonly value: string
  readonly name: string
  readonly suffix: string
}

const accent = '#ff5700'

const styles = {
  wrapper: css`
    // display: inline-block;
    width: calc(100% - 160px);
    position: relative;

    .ant-select-selection-placeholder {
      text-align: left;
    }

    .ant-select-dropdown {
      bottom: 100%;
      top: auto;
    }

    @media (max-width: 768px) {
      width: calc(100% - 80px);
    }
  `,
  input: css`
    padding: 0.25rem 0.5rem;
    width: 12rem;
  `,
  list: css`
    background-color: #fff;
    border: 1px solid ${accent};
    display: block;
    list-style: none;
    margin: 0;
    padding: 0;
    position: absolute;
    width: 12rem;
    z-index: 2;
  `,
  item: css`
    border-bottom: 1px dotted ${accent};
    margin: 0;
  `,
  link: css`
    display: block;
    padding: 0.25rem 0.5rem;
  `,
  footer: css`
    font-size: 0.75rem;
    margin: 0;
    padding: 0.5rem;
    border: 0;
  `,
  hidden: css`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  `,
  destinationModalContainer: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  `,
  destinationsHeading: css`
    border-radius: 4px;
    word-wrap: break-word;
    max-width: 80%;
    min-width: 30%;
    color: black;
    background-color: #ccccff;
    font-size: 17px;
    padding: 8px 15px;
    text-align: center;
  `,
  destinationUl: css`
    list-style-type: none;
    padding: 0;
    margin: 10px 5px;
    max-height: 350px;
    overflow: auto;
  `,
  destinationsLi: css`
    background: #f0f0f0;
    margin: 0.5rem 0;
    padding: 0.75rem;
    border-radius: 5px;
    font-size: 1.1rem;
    transition: background 0.3s;
    color: black;
    font-weight: medium;
    text-align: left;
    cursor: pointer;
  `,
  destinationsInput: css`
    width: 100%;
    position: relative;
    background-color: #fff;
    border-radius: 4px 0px 0px 4px;
    box-shadow: none;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    min-height: 38px;
    outline: none;
    box-sizing: border-box;
    border: 1px solid rgb(204, 204, 204);
    padding-left: 10px;
    padding-right: 10px;
  `,
  modalButton: css`
    width: 100%;
    background-color: white;
    border-radius: 4px 0px 0px 4px;
    box-shadow: none;
    min-height: 38px;
    border: 1px solid rgb(204, 204, 204);
    padding-left: 10px;
    padding-right: 10px;
    text-align: left;
  `
}

const customStyles = {
  control: (provided: any, state: any) => ({
    ...provided,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    cursor: 'pointer',
    outline: 'none',
    // // This line disable the blue border
    border: '1px solid #CCCCCC',
    boxShadow: 'none',
    '&:hover': {
      border: '1px solid #CCCCCC'
    }
  }),

  menu: (provided: any, state: any) => ({
    ...provided,
    textAlign: 'left',
    '@media (max-width: 480px)': {
      width: 'calc(100% + 80px)',
      height: '150px',
      overflow: 'hidden'
    }
  }),

  option: (provided: any, state: any) => ({
    ...provided,
    cursor: 'pointer'
  })
}

const constructQuery = (query: string) =>
  query
    .split(' ')
    .map(term => {
      const t = term.trim()
      return t ? `name:${t}*^100` : ''
    })
    .join(' ')

const constructNameQuery = (query: string) =>
  query
    .split(' ')
    .map(term => {
      const t = term.trim()
      return t ? `name:${t}` : ''
    })
    .join(' ')

interface DestinationSearchProps {
  onChange(dest: SearchResult): void
  destinationsState: any
}

export const DestinationSearch = ({ onChange }: DestinationSearchProps) => {
  return (
    <DestinationsContext.Consumer>
      {value => (
        <DestinationSearchCtrl
          onChange={onChange}
          destinationsState={value}
        ></DestinationSearchCtrl>
      )}
    </DestinationsContext.Consumer>
  )
}

export const DestinationSearchCtrl = ({ onChange, destinationsState }: DestinationSearchProps) => {
  const [results, setResults] = useState<readonly SearchResult[]>()
  const [isLoading, setLoading] = useState(true)
  const defaultDest = usePickupDestination(destinationsState)

  useEffect(() => {
    console.log("Destinations State in DestinationSearch effect: ", destinationsState);
    
    if (destinationsState && destinationsState.store) {
      setResults(destinationsState.topDestinations)
      setLoading(false)
    }
  }, [destinationsState]) // empty dependency array makes this only run once onload //https://www.robinwieruch.de/react-hooks-fetch-data

  const search = (query: string): readonly SearchResult[] => {
    console.log("DestinationsState within search in DestinationSearch: ", destinationsState);
    console.log("Query inside search: ", query);
    
    const { idx, store, topDestinations } = destinationsState
    if (query) {
      const exactNameMatchQuery = constructNameQuery(query)
      const partialMatchQuery = constructQuery(query)

      console.log("Exact Name Match Query: ", exactNameMatchQuery, ", Partial Match Query: ", partialMatchQuery);

      const matched: any = {}
      const exactMatch = idx.search(exactNameMatchQuery).map(({ ref }: { ref: number }) => {
        matched[store[ref].id] = true
        return {
          label: store[ref].label,
          value: store[ref].id,
          ...store[ref]
        }
      })

      const partialMatch = idx
        .search(partialMatchQuery)
        .map(({ ref }: { ref: number }) => {
          return {
            label: store[ref].label,
            value: store[ref].id,
            ...store[ref]
          }
        })
        .filter((match: any) => !matched[match.value])

        console.log("exactMatch and partialMatch in search: ", exactMatch, partialMatch);

      return [...exactMatch, ...partialMatch]
    }
    return topDestinations
  }

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      <DestinationsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div css={styles.destinationModalContainer}>
          <p css={styles.destinationsHeading}>
            Where should we pick you up?
          </p>
          <input type='text'
            placeholder='Enter city here...'
            css={styles.destinationsInput}
            onChange={(e) => {
              setResults(search(e.target.value))
            }}
            autoFocus={true}
          />

          <ul css={styles.destinationUl}>
            {
              results?.length ? results.map((current, idx) => (
                <li key={current.label}
                  css={styles.destinationsLi}
                  onClick={() => onChange(current)}
                >
                  {current.label}
                </li>
              )) : <></>
            }
          </ul>
        </div>
      </DestinationsModal>
      <div css={styles.wrapper}>
        {
          isMobile ?
            <button
              type='button'
              onClick={() => setIsModalOpen(prev => !prev)}
              css={styles.modalButton}
            >
              Enter city here...
            </button>
            :
            <Select
              isDisabled={isLoading}
              isLoading={isLoading}
              styles={customStyles}
              style={{ width: '100%', position: 'absolute', top: 0, left: 0 }}
              options={results}
              onInputChange={(str, action) => {
                if (action.action === 'input-change') setResults(search(str))
              }}
              onChange={obj => {
                onChange(obj)
              }}
              placeholder="Enter city here..."
              menuPlacement="top"
              menuShouldScrollIntoView={true}
              value={defaultDest}
            ></Select>
        }
      </div>
    </>
  )
}
